import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  Download, 
  FileText, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  FileCheck,
  AlertCircle,
  Calendar,
  CheckCircle,
  BarChart3
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { toast } from 'sonner';

// UAE VAT Standard Reports Data
const monthlyRevenueData = [
  { month: 'Jan', sales: 245000, purchase: 132000, vat: 12250, net: 112750 },
  { month: 'Feb', sales: 268000, purchase: 145000, vat: 13400, net: 123000 },
  { month: 'Mar', sales: 289000, purchase: 156000, vat: 14450, net: 133000 },
  { month: 'Apr', sales: 312000, purchase: 167000, vat: 15600, net: 145000 },
  { month: 'May', sales: 334000, purchase: 178000, vat: 16700, net: 156000 },
  { month: 'Jun', sales: 356000, purchase: 189000, vat: 17800, net: 167000 },
  { month: 'Jul', sales: 378000, purchase: 198000, vat: 18900, net: 180000 },
  { month: 'Aug', sales: 392000, purchase: 205000, vat: 19600, net: 187000 },
  { month: 'Sep', sales: 415000, purchase: 218000, vat: 20750, net: 197000 },
  { month: 'Oct', sales: 438000, purchase: 230000, vat: 21900, net: 208000 },
];

const vatSummaryData = [
  { category: 'Standard Rate (5%)', amount: 21900, percentage: 87 },
  { category: 'Zero Rated', amount: 2100, percentage: 8 },
  { category: 'Exempt', amount: 1250, percentage: 5 },
];

const invoiceStatusData = [
  { status: 'Processed', count: 1247, value: 438000, color: '#36B37E' },
  { status: 'Pending', count: 23, value: 12340, color: '#FFAB00' },
  { status: 'Failed', count: 5, value: 2890, color: '#FF5630' },
  { status: 'Rejected', count: 2, value: 1120, color: '#DE350B' },
];

const topVendorsData = [
  { name: 'Acme Corporation', invoices: 156, amount: 89450 },
  { name: 'Tech Ventures LLC', invoices: 134, amount: 78230 },
  { name: 'Global Trading FZ', invoices: 98, amount: 56780 },
  { name: 'Smart Solutions', invoices: 87, amount: 45620 },
  { name: 'Digital Services Co', invoices: 76, amount: 38910 },
];

const complianceData = [
  { metric: 'On-time Submissions', current: 98.5, previous: 97.2, status: 'good' },
  { metric: 'VAT Accuracy', current: 99.8, previous: 99.5, status: 'excellent' },
  { metric: 'Data Completeness', current: 97.3, previous: 96.8, status: 'good' },
  { metric: 'FTA Acceptance Rate', current: 96.7, previous: 95.4, status: 'good' },
];

const taxReportData = [
  {
    period: 'Q3 2025',
    salesVat: 58450,
    purchaseVat: 32100,
    netVat: 26350,
    status: 'Filed',
    filedDate: '2025-10-15',
    dueDate: '2025-10-28'
  },
  {
    period: 'Q2 2025',
    salesVat: 54200,
    purchaseVat: 29800,
    netVat: 24400,
    status: 'Filed',
    filedDate: '2025-07-18',
    dueDate: '2025-07-28'
  },
  {
    period: 'Q1 2025',
    salesVat: 49600,
    purchaseVat: 27300,
    netVat: 22300,
    status: 'Filed',
    filedDate: '2025-04-16',
    dueDate: '2025-04-28'
  },
];

export default function ReportsSection() {
  const [selectedPeriod, setSelectedPeriod] = useState('2025');
  const [selectedQuarter, setSelectedQuarter] = useState('Q4');

  const handleExport = (reportType: string) => {
    toast.success(`Exporting ${reportType} report...`);
  };

  const totalSales = monthlyRevenueData.reduce((sum, item) => sum + item.sales, 0);
  const totalPurchases = monthlyRevenueData.reduce((sum, item) => sum + item.purchase, 0);
  const totalVAT = monthlyRevenueData.reduce((sum, item) => sum + item.vat, 0);
  const totalNet = monthlyRevenueData.reduce((sum, item) => sum + item.net, 0);

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-lg blur-md opacity-50"></div>
            <div className="relative p-2 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-lg premium-shadow">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-3xl text-gray-900">Financial Reports & Analytics</h1>
            <p className="text-gray-600 mt-1">UAE VAT compliant reports and business intelligence</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2024">2024</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg" onClick={() => handleExport('Comprehensive')}>
            <Download className="w-4 h-4 mr-2" />
            Export All
          </Button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Sales</p>
                <p className="text-3xl text-gray-900">AED {totalSales.toLocaleString()}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">12.5% vs last year</span>
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Purchases</p>
                <p className="text-3xl text-gray-900">AED {totalPurchases.toLocaleString()}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 text-blue-500 mr-1" />
                  <span className="text-sm text-blue-600">8.3% vs last year</span>
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">VAT Collected</p>
                <p className="text-3xl text-gray-900">AED {totalVAT.toLocaleString()}</p>
                <div className="flex items-center mt-2">
                  <FileCheck className="w-4 h-4 text-purple-500 mr-1" />
                  <span className="text-sm text-gray-600">5% Standard Rate</span>
                </div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <FileCheck className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Net Position</p>
                <p className="text-3xl text-gray-900">AED {totalNet.toLocaleString()}</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">Positive</span>
                </div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Reports Tabs */}
      <Tabs defaultValue="financial" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="financial">Financial</TabsTrigger>
          <TabsTrigger value="vat">VAT Reports</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
        </TabsList>

        {/* Financial Report */}
        <TabsContent value="financial" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Monthly Revenue & Expenses Trend</CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('Monthly Revenue')}>
                  <Download className="w-3 h-3 mr-1" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={monthlyRevenueData}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#36B37E" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#36B37E" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorPurchase" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF5630" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#FF5630" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="sales" stroke="#36B37E" fillOpacity={1} fill="url(#colorSales)" name="Sales (AED)" />
                  <Area type="monotone" dataKey="purchase" stroke="#FF5630" fillOpacity={1} fill="url(#colorPurchase)" name="Purchases (AED)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Invoice Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={invoiceStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {invoiceStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {invoiceStatusData.map((item) => (
                    <div key={item.status} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                        <span className="text-sm text-gray-700">{item.status}</span>
                      </div>
                      <span className="text-sm text-gray-900">{item.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top 5 Vendors by Value</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topVendorsData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip />
                    <Bar dataKey="amount" fill="#0052CC" name="Amount (AED)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* VAT Report */}
        <TabsContent value="vat" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>VAT Analysis - UAE Federal Tax Authority Standard</CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('VAT Report')}>
                  <Download className="w-3 h-3 mr-1" />
                  Export VAT Return
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="vat" stroke="#0052CC" strokeWidth={3} name="VAT Collected (AED)" />
                  <Line type="monotone" dataKey="net" stroke="#36B37E" strokeWidth={3} name="Net Amount (AED)" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>VAT by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {vatSummaryData.map((item) => (
                    <div key={item.category}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-700">{item.category}</span>
                        <span className="text-sm text-gray-900">AED {item.amount.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[#0052CC] h-2 rounded-full" 
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600">{item.percentage}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>VAT Return Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-600">Output VAT (Sales)</p>
                      <p className="text-xl text-gray-900 mt-1">AED 21,900</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-600">Input VAT (Purchases)</p>
                      <p className="text-xl text-gray-900 mt-1">AED 11,500</p>
                    </div>
                    <TrendingDown className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                    <div>
                      <p className="text-sm text-gray-600">Net VAT Payable to FTA</p>
                      <p className="text-2xl text-gray-900 mt-1">AED 10,400</p>
                    </div>
                    <FileCheck className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Compliance Report */}
        <TabsContent value="compliance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>FTA Compliance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                {complianceData.map((item) => (
                  <div key={item.metric} className="p-6 border rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-gray-900">{item.metric}</h3>
                      {item.status === 'excellent' && <Badge className="bg-green-500">Excellent</Badge>}
                      {item.status === 'good' && <Badge className="bg-blue-500">Good</Badge>}
                    </div>
                    <div className="flex items-end space-x-4">
                      <div>
                        <p className="text-3xl text-gray-900">{item.current}%</p>
                        <p className="text-sm text-gray-600">Current Period</p>
                      </div>
                      <div className="flex items-center text-green-600 mb-1">
                        <TrendingUp className="w-4 h-4 mr-1" />
                        <span className="text-sm">+{(item.current - item.previous).toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${item.current}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent FTA Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Sales VAT</TableHead>
                    <TableHead>Purchase VAT</TableHead>
                    <TableHead>Net VAT</TableHead>
                    <TableHead>Filed Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxReportData.map((report) => (
                    <TableRow key={report.period}>
                      <TableCell className="text-gray-900">{report.period}</TableCell>
                      <TableCell>AED {report.salesVat.toLocaleString()}</TableCell>
                      <TableCell>AED {report.purchaseVat.toLocaleString()}</TableCell>
                      <TableCell className="text-gray-900">AED {report.netVat.toLocaleString()}</TableCell>
                      <TableCell>{report.filedDate}</TableCell>
                      <TableCell>{report.dueDate}</TableCell>
                      <TableCell>
                        <Badge className="bg-green-500">{report.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Business Intelligence Dashboard</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="sales" fill="#36B37E" name="Sales (AED)" />
                  <Bar dataKey="purchase" fill="#FF5630" name="Purchases (AED)" />
                  <Bar dataKey="net" fill="#0052CC" name="Net (AED)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Average Invoice Value</p>
                  <p className="text-3xl text-gray-900 mt-2">AED 2,345</p>
                  <div className="flex items-center justify-center mt-2">
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-sm text-green-600">+5.2%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Processing Time (Avg)</p>
                  <p className="text-3xl text-gray-900 mt-2">2.3 mins</p>
                  <div className="flex items-center justify-center mt-2">
                    <TrendingDown className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-sm text-green-600">-12%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Success Rate</p>
                  <p className="text-3xl text-gray-900 mt-2">98.5%</p>
                  <div className="flex items-center justify-center mt-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                    <span className="text-sm text-green-600">Excellent</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Quarterly Report */}
        <TabsContent value="quarterly" className="space-y-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl text-gray-900">Quarterly Tax Report</h2>
              <p className="text-gray-600">UAE FTA Compliant Quarterly Submission</p>
            </div>
            <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Q4">Q4 2025</SelectItem>
                <SelectItem value="Q3">Q3 2025</SelectItem>
                <SelectItem value="Q2">Q2 2025</SelectItem>
                <SelectItem value="Q1">Q1 2025</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <Calendar className="w-8 h-8 text-gray-600 mb-3" />
                <p className="text-sm text-gray-600">Period</p>
                <p className="text-xl text-gray-900 mt-1">Q4 2025</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <FileText className="w-8 h-8 text-blue-600 mb-3" />
                <p className="text-sm text-gray-600">Total Invoices</p>
                <p className="text-xl text-gray-900 mt-1">3,742</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <DollarSign className="w-8 h-8 text-green-600 mb-3" />
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-xl text-gray-900 mt-1">AED 1.24M</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <FileCheck className="w-8 h-8 text-purple-600 mb-3" />
                <p className="text-sm text-gray-600">VAT Payable</p>
                <p className="text-xl text-gray-900 mt-1">AED 62,000</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Quarterly Financial Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-6 p-6 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Standard Rated Supplies</p>
                    <p className="text-2xl text-gray-900">AED 1,238,000</p>
                    <p className="text-sm text-gray-600 mt-1">VAT @ 5%: AED 61,900</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Zero Rated Supplies</p>
                    <p className="text-2xl text-gray-900">AED 42,000</p>
                    <p className="text-sm text-gray-600 mt-1">VAT @ 0%: AED 0</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Exempt Supplies</p>
                    <p className="text-2xl text-gray-900">AED 25,000</p>
                    <p className="text-sm text-gray-600 mt-1">No VAT</p>
                  </div>
                </div>

                <div className="p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Net VAT Due for Quarter</p>
                      <p className="text-3xl text-gray-900 mt-2">AED 62,000</p>
                      <p className="text-sm text-gray-600 mt-2">Due Date: 28 October 2025</p>
                    </div>
                    <Button className="bg-[#0052CC]" onClick={() => handleExport('Quarterly VAT Return')}>
                      <Download className="w-4 h-4 mr-2" />
                      Download Return
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
